// fastTrackAddon.js
// "Priority Physician Review" — the paid fast-track add-on.
//
// It is a normal catalog product from /api/get-all-products. Nothing about it is
// hardcoded here except the internal_id used to find it: the id, the name and the
// price are always read off the live record at runtime.
//
// It is NOT part of checkout. The pre-ticked box that used to add it to the cart
// is gone — it cost conversion on the order itself — and no checkout payload
// carries this fee any more. It is offered AFTER the order is captured and
// charged separately: see FastTrackUpsellModal.vue and fastTrackUpsell.js.
//
// `is_active` on the product is the ONLY on/off switch. Flip it false in the
// catalog and the offer disappears everywhere with no deploy.
//
// Deliberately imports nothing: productData.js imports isFastTrackProduct from
// here, so reaching back into it for the catalog would make the two modules
// circular. The loader that needs both lives in fastTrackUpsell.js.

/** The live catalog value. It was `fast_track` until the backend recased it. */
export const FAST_TRACK_INTERNAL_ID = 'FAST_TRACK';

/**
 * Matches the live `FAST_TRACK`, and stays case-insensitive/trimmed so the old
 * `fast_track` spelling still resolves. `internal_id` is a free-text field edited
 * by hand in the catalog and has already flipped once — an exact match silently
 * turns the offer off, and silently lets the add-on leak into the upsell list and
 * the shop grids, which filter on this same predicate.
 */
export const isFastTrackProduct = (product) =>
    typeof product?.internal_id === 'string' &&
    product.internal_id.trim().toUpperCase() === FAST_TRACK_INTERNAL_ID;

/**
 * Resolve the add-on from a catalog array.
 * Returns { product, plan } or null.
 *
 * Whether the offer is made is decided by `is_active` and nothing else — the
 * plan's `terms` is deliberately not read, so changing it can never hide the
 * add-on or switch it off by accident.
 *
 * null means the add-on is not offerable — callers must show no offer at all.
 * There is deliberately no fallback price and no fallback product id.
 */
export const resolveFastTrackAddon = (products) => {
    if (!Array.isArray(products)) return null;

    const product = products.find(p => isFastTrackProduct(p) && Boolean(p.is_active));
    if (!product) return null;

    const plan = (product.pricing_plans || []).find(
        p => p && Boolean(p.is_active) && p.price != null
    );
    if (!plan) return null;

    return { product, plan };
};
